import { IYearMonth } from '../date';
import { DateService } from '../services/dateService';
import { IBusiness } from './businessBase';

/**
 * 販売実績の情報
 * @property amount - 販売実績数量
 */
interface ISalesResult extends IYearMonth {
  amount: number;
}

export const salesResultsProperties = ['salesResults'] as const;

/**
 * SalesResultsのプロパティ名を取得する
 * @returns SalesResultsのプロパティ名
 */
type SalesResultsPropertiesType = { [K in (typeof salesResultsProperties)[number]]: unknown };

/**
 * 販売実績をまとめた型
 */
export interface SalesResults {
  businessId: string;
  salesResults: ISalesResult[];
}

/**
 * ISalesResultであることを保証する (ISalesResultのプロパティを持っているかどうか)
 * @param value - 任意の値
 */
function isSalesResults(value: any): value is SalesResults {
  return (
    Object.prototype.hasOwnProperty.call(value, 'businessId') &&
    typeof value.businessId === 'string' &&
    Object.prototype.hasOwnProperty.call(value, 'salesResults') &&
    Array.isArray(value.salesResults)
  );
}

/**
 * SalesResultsを初期化する
 * @param value - SalesResults & { businessId: string }
 */
function initSalesResults(value: Partial<SalesResults> & { businessId: string }): asserts value is SalesResults {
  value.salesResults = [];
}

/**
 * SalesResultsを初期化する (多分使ってない)
 * @param businessId - 取引ID
 */
export function getinitializedSalesResults(businessId: string): SalesResults {
  const value = { businessId: businessId };
  initSalesResults(value);
  return value;
}

/**
 * 販売実績のヘッダーを取得する
 * @param businesses - 取引情報
 */
export function getSalesResultsHeaders(businesses: IBusiness[]): ISalesResult[] {
  const map = new Map<string, ISalesResult>();
  for (let i = 0; i < businesses.length; i++) {
    const targetSalesResults = businesses[i]?.salesResults ?? [];
    for (let j = 0; j < targetSalesResults.length; j++) {
      map.set(DateService.serialize(targetSalesResults[j]), targetSalesResults[j]);
    }
  }
  return Array.from(map.values()).sort((a, b) => a.year * 1000 + a.month - (b.year * 1000 + b.month));
}
/**
 * targetがgetArrivalHeadersで取得できるheaderと同一かどうかを調べる
 * @param target - 調べる対象
 * @param salesResultsHeaders - 調べる対象のヘッダー
 */
export function assertSalesResults(target: IYearMonth, salesResultsHeaders: IYearMonth[]): boolean {
  // 実装時点では、線形探索(O(N))でも大丈夫なデータ構造の想定
  // 実際に実行される計算量はbusinessesをMとした場合、M*2*O(N) => O(N*M)
  return salesResultsHeaders.map((a) => DateService.serialize(a)).includes(DateService.serialize(target));
}

/**
 * SalesResultsを部分型として持っていることを保証する
 * @param value - 任意の値
 */
function assertParticalSalesResults<T>(value: T): asserts value is T & SalesResults {
  if (!isSalesResults(value)) {
    throw Error('SalesResultsを部分型として持っていません');
  }
}

export { ISalesResult, isSalesResults, initSalesResults, assertParticalSalesResults };
